import { useMemo, VFC } from 'react';
import { Controller } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';

import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import ReactSelect from 'dashboard/components/ReactSelect';
import { AccountTypesFilterOption } from 'dashboard/models/account-type';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface FormCreateAccountProps {
  accountTypesFilterOptions: AccountTypesFilterOption[];
  closeModal: () => void;
  submit: (formData: FormCreateAccountData) => void;
}

const FormCreateAccount: VFC<FormCreateAccountProps> = ({
  accountTypesFilterOptions,
  closeModal,
  submit,
}) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting, isValid, errors },
  } = useFormSecurely({
    defaultValues: {
      referenceId: '',
      accountTypeId: (
        accountTypesFilterOptions.find((opt) => opt['is-legacy-account-type']) ??
        accountTypesFilterOptions[0]
      )?.id,
      nameFirst: undefined,
      nameMiddle: undefined,
      nameLast: undefined,
    },
    mode: 'onChange',
  });

  const selectedAccountTypeId: string = watch('accountTypeId');
  const selectedAccountType = useMemo(() => {
    return accountTypesFilterOptions.find((opt) => opt.id === selectedAccountTypeId);
  }, [accountTypesFilterOptions, selectedAccountTypeId]);

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Label id='form-create-account__reference_id_label' size='md' weight='bold'>
        Reference ID (required)
      </Label>
      <Input
        {...register('referenceId', {
          required: 'Account must have a reference ID.',
          maxLength: {
            value: 255,
            message: 'Reference IDs can be no more than 255 characters.',
          },
        })}
        disabled={isSubmitting}
        role='input'
        aria-labelledby='form-create-account__reference_id_label'
        data-test='create-account-form__reference-id'
      />
      {errors['referenceId'] && <FormError>{errors['referenceId'].message}</FormError>}

      <Spacer height={1} />

      <FeatureFlagged featureFlag='dashboard:accounts-show-account-type'>
        <Label id='form-create-account__account-type-label' size='md' weight='bold'>
          Account Type
        </Label>
        <Controller
          name='accountTypeId'
          control={control}
          render={({ field }) => (
            <ReactSelect
              {...field}
              id='form-create-account__select-account-type-id'
              aria-labelledby='form-create-account__account-type-label'
              disabled={isSubmitting}
              options={accountTypesFilterOptions.map((option) => ({
                label: option.name,
                value: option.id,
              }))}
              portal
            />
          )}
        />
      </FeatureFlagged>

      {selectedAccountType?.['is-legacy-account-type'] && (
        <>
          <Spacer height={1} />
          <Label id='form-create-account__name_label' size='md' weight='bold'>
            Name (optional)
          </Label>
          <HStack equal>
            <Input
              {...register('nameFirst')}
              disabled={isSubmitting}
              role='input'
              aria-labelledby='form-create-account__name_label'
              placeholder='First'
            />
            <Input
              {...register('nameMiddle')}
              disabled={isSubmitting}
              role='input'
              aria-labelledby='form-create-account__name_label'
              placeholder='Middle'
            />
            <Input
              {...register('nameLast')}
              disabled={isSubmitting}
              role='input'
              aria-labelledby='form-create-account__name_label'
              placeholder='Last'
            />
          </HStack>
        </>
      )}

      <Spacer height={3} />

      <HStack equal>
        <Button id='create-account-form__close' onClick={closeModal} variant='basic'>
          Cancel
        </Button>
        <Button
          type='submit'
          data-test='create-account-form__submit'
          disabled={!isValid}
          value='Submit'
        >
          Create Account
        </Button>
      </HStack>
    </form>
  );
};

export interface FormCreateAccountData {
  referenceId: string;
  accountTypeId: string;
  nameFirst?: string;
  nameMiddle?: string;
  nameLast?: string;
}

export default FormCreateAccount;
